/**
 * @fileoverview InputRenderer component for the Storyteller web application.
 * Renders an input form element with a label, input field, and suggestions dropdown.
 *
 * @example
 * // Basic usage
 * <InputRenderer label="Input Label" path="input-path" />
 */

import { ControlProps, isNumberControl, isStringControl, or, rankWith } from '@jsonforms/core'
import { withJsonFormsControlProps } from '@jsonforms/react'
import { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import css from './InputRenderer.module.css'


/**
 * @interface InputProps
 * @description Interface for the Input component props.
 * @param {ControlProps} props - The props for the Input component.
 * @returns {JSX.Element} The Input component.
 */
interface InputProps extends ControlProps {
    data: string
    label: string
    required?: boolean
    handleChange(path: string, value: string): void
    path: string
}

/**
 * @function classNames
 * @description Helper function to join class names with spaces.
 * @param {...string[]} classes - The class names to join.
 * @returns {string} The joined class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function Input
 * @description Component for rendering an input form element.
 * @param {InputProps} props - The props for the Input component.
 * @returns {JSX.Element} The Input component.
 */
function Input(props: InputProps) {
    const { uischema, errors, data = '', handleChange, path, required } = props
    const { t } = useTranslation()
    const hasError = errors.length > 0

    return (
        <>
            <input
                name={path}
                id={path}
                type={uischema.options?.type}
                placeholder={t(uischema.options?.placeholder)}
                className={classNames(css.input, hasError && css.error)}
                value={data}
                required={required}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(path, e.target.value)}
            />
            {hasError && <span className={css.errorMessage}>{errors}</span>}
        </>
    )
}

export const InputTester = rankWith(
    3, //increase rank as needed
    or(isNumberControl, isStringControl)
)

export default withJsonFormsControlProps(Input)
